import type { Graphics } from 'pixi.js';
import { type RngState, nextFloat } from '../../shared/prng';
import type { Vec2 } from '../../sim/state';
import { OVERFLOW } from '../ground-layout';
import { parseHexColor } from '../palette';
import { drawTreeLine } from './path-trees';
import { type Way, at, shoreAt, stoneLanterns, trailAt } from '../../data/full/path-layout';
import { PATH_COLORS } from './path-way';

export const LAKE = {
  water: parseHexColor('#243f70'),
  deep: parseHexColor('#1a2e58'),
  reflect: parseHexColor('#ff8fb0'),
  mud: parseHexColor('#4a3a34'),
  ripple: parseHexColor('#ffffff'),
};
const DIRT = { ground: parseHexColor('#6e5040'), spot: parseHexColor('#ffc890'), half: 54 };
export const STONE = {
  base: parseHexColor('#7a7f80'),
  cap: parseHexColor('#9a9fa0'),
  edge: parseHexColor('#2e3232'),
  flame: parseHexColor('#ffe6a8'),
};
const FOREST_IN = 50;
const LAKE_FLIES_EVERY = 90;

export function drawShores(g: Graphics, way: Way, rng: RngState): Vec2[] {
  drawTreeLine(g, way, -1, rng, -60);
  drawTreeLine(g, way, -1, rng, FOREST_IN);
  const far: Vec2[] = [];
  const shore: Vec2[] = [];
  for (let along = -OVERFLOW; along <= way.length + OVERFLOW; along += 20) {
    shore.push(at(way, along, shoreAt(way, along)));
    far.unshift(at(way, along, way.width + OVERFLOW));
  }
  const flat = (points: Vec2[]) => points.flatMap(({ x, y }) => [x, y]);
  g.poly(flat([...shore, ...far])).fill(LAKE.mud);
  const wet = shore.map((point) => ({
    x: point.x + (way.down ? 12 : 0),
    y: point.y + (way.down ? 0 : 12),
  }));
  g.poly(flat([...wet, ...far])).fill(LAKE.water);
  const deep: Vec2[] = [];
  for (let along = -OVERFLOW; along <= way.length + OVERFLOW; along += 40) {
    deep.push(at(way, along, shoreAt(way, along) + 120));
  }
  g.poly(flat([...deep, ...far])).fill({ color: LAKE.deep, alpha: 0.35 });
  for (let along = -OVERFLOW; along < way.length + OVERFLOW; along += 34) {
    const across = shoreAt(way, along) + 24 + nextFloat(rng) * 150;
    const from = at(way, along, across);
    const to = at(way, along + 30 + nextFloat(rng) * 120, across);
    g.moveTo(from.x, from.y)
      .lineTo(to.x, to.y)
      .stroke({
        width: 2 + nextFloat(rng) * 3,
        color: LAKE.reflect,
        alpha: 0.3 + nextFloat(rng) * 0.35,
        cap: 'round',
      });
  }
  const flies: Vec2[] = [];
  for (let along = 0; along < way.length; along += LAKE_FLIES_EVERY) {
    const spot = at(
      way,
      along + nextFloat(rng) * LAKE_FLIES_EVERY,
      shoreAt(way, along) + 20 + nextFloat(rng) * 130,
    );
    g.circle(spot.x, spot.y, 2).fill(PATH_COLORS.firefly);
    flies.push(spot);
  }
  return flies;
}

export function drawDirt(g: Graphics, way: Way, rng: RngState): void {
  const near: Vec2[] = [];
  const far: Vec2[] = [];
  for (let along = -OVERFLOW; along <= way.length + OVERFLOW; along += 20) {
    const half = DIRT.half + 10 * Math.sin(along / 140);
    near.push(at(way, along, trailAt(way, along) - half - nextFloat(rng) * 8));
    far.unshift(at(way, along, trailAt(way, along) + half + nextFloat(rng) * 8));
  }
  g.poly([...near, ...far].flatMap(({ x, y }) => [x, y])).fill(DIRT.ground);
  for (let along = -OVERFLOW; along < way.length + OVERFLOW; along += 18) {
    const { x, y } = at(way, along, trailAt(way, along) + (nextFloat(rng) - 0.5) * 80);
    g.ellipse(x, y, 6 + nextFloat(rng) * 14, 4 + nextFloat(rng) * 7).fill({
      color: DIRT.spot,
      alpha: 0.18 + nextFloat(rng) * 0.25,
    });
  }
}

export function drawStoneLanterns(g: Graphics, way: Way): Vec2[] {
  const lamps = stoneLanterns(way);
  for (const { x, y } of lamps) {
    g.circle(x, y, 15).fill(STONE.base).stroke({ width: 2.5, color: STONE.edge });
    g.poly([x, y - 13, x + 13, y, x, y + 13, x - 13, y])
      .fill(STONE.cap)
      .stroke({ width: 2, color: STONE.edge });
    g.circle(x, y, 4.5).fill(STONE.flame);
  }
  return lamps;
}
